import { Add, Close, Delete, Edit as EditIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Award,
  CheckCircle2,
  CreditCard,
  Gem,
  Handshake,
  Heart,
  Music4,
  Shirt,
  TrendingUp,
  Utensils,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import logo from "../../../assets/logo_no_background.png";
import {
  createWelcomeContent,
  deleteWelcomeContent,
  getWelcomeContent,
  updateWelcomeContent,
  uploadWelcomeImage,
} from "../../../services/welcome.service";
import { useToast } from "../../../ui-components/ToastProvider";

// --- Types & Constants ---

interface WelcomeContent {
  id: number;
  section: string;
  title: string;
  description: string;
  image_url: string;
  icon: string;
  display_order: number;
  is_active: boolean;
}

const sections = [
  { id: "hero", label: "Hero (Topo)" },
  { id: "stats", label: "Estatísticas" },
  { id: "benefits", label: "Benefícios" },
  { id: "categories", label: "Categorias" },
  { id: "gallery", label: "Galeria" },
  { id: "about", label: "Sobre (Boa Companhia)" },
  { id: "organization", label: "Organização" },
  { id: "impact", label: "Impacto Social" },
  { id: "footer", label: "Rodapé" },
];

const iconMap: any = {
  Award,
  CheckCircle2,
  CreditCard,
  Gem,
  Handshake,
  Heart,
  Music4,
  Shirt,
  TrendingUp,
  Utensils,
  X,
};

// --- Helper Components ---

const RenderIcon = ({
  iconName,
  className,
}: {
  iconName: string;
  className?: string;
}) => {
  const IconComponent = iconMap[iconName] || CheckCircle2;
  return <IconComponent className={className || "w-5 h-5"} />;
};

const SectionOverlay = ({
  title,
  onEdit,
}: {
  title: string;
  onEdit: () => void;
}) => (
  <div
    className="absolute inset-0 bg-blue-500/10 border-2 border-blue-500 border-dashed opacity-0 hover:opacity-100 transition-opacity z-50 flex items-center justify-center rounded-lg cursor-pointer"
    onClick={(e) => {
      e.stopPropagation();
      onEdit();
    }}
  >
    <Button
      variant="contained"
      startIcon={<EditIcon />}
      className="shadow-lg"
      sx={{ textTransform: "none", fontWeight: "bold" }}
    >
      Editar {title}
    </Button>
  </div>
);

// --- Manager Component (Table + Dialog) ---

const SectionManager = ({
  sectionId,
  open,
  onClose,
  onUpdate,
}: {
  sectionId: string;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}) => {
  const [data, setData] = useState<WelcomeContent[]>([]);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<WelcomeContent | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image_url: "",
    icon: "",
    display_order: 0,
  });

  const fetchData = useCallback(async () => {
    try {
      const result = await getWelcomeContent(sectionId);
      // Sort by display_order
      const sortedResult = result.sort(
        (a: WelcomeContent, b: WelcomeContent) =>
          a.display_order - b.display_order
      );
      setData(sortedResult);
    } catch (error) {
      console.error(error);
      showToast("Erro ao carregar dados", { severity: "error" });
    }
  }, [sectionId, showToast]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const handleOpenForm = (item?: WelcomeContent) => {
    setSelectedFile(null);
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title || "",
        description: item.description || "",
        image_url: item.image_url || "",
        icon: item.icon || "",
        display_order: item.display_order || 0,
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: "",
        description: "",
        image_url: "",
        icon: "",
        display_order: data.length + 1,
      });
    }
    setOpenFormDialog(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleSave = async () => {
    try {
      let finalImageUrl = formData.image_url;
      if (selectedFile) {
        const uploadResult = await uploadWelcomeImage(selectedFile);
        finalImageUrl = uploadResult.url;
      }

      // Logic to swap order if conflict exists
      const targetOrder = Number(formData.display_order);
      if (editingItem && editingItem.display_order !== targetOrder) {
        const conflictingItem = data.find(
          (item) =>
            item.display_order === targetOrder && item.id !== editingItem.id
        );

        if (conflictingItem) {
          // Swap: Move conflicting item to the old position of the editing item
          await updateWelcomeContent(conflictingItem.id, {
            ...conflictingItem,
            display_order: editingItem.display_order,
          });
          showToast(
            `Ordem trocada com item "${
              conflictingItem.title || "ID: " + conflictingItem.id
            }"`,
            { severity: "info" }
          );
        }
      }

      const payload = {
        section: sectionId,
        ...formData,
        image_url: finalImageUrl,
      };

      if (editingItem) {
        await updateWelcomeContent(editingItem.id, payload);
        showToast("Atualizado com sucesso", { severity: "success" });
      } else {
        await createWelcomeContent(payload);
        showToast("Criado com sucesso", { severity: "success" });
      }
      setOpenFormDialog(false);
      fetchData();
      onUpdate(); // Trigger parent refresh
    } catch (error) {
      console.error(error);
      showToast("Erro ao salvar", { severity: "error" });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Tem certeza que deseja excluir?")) {
      try {
        await deleteWelcomeContent(id);
        showToast("Excluído com sucesso", { severity: "success" });
        fetchData();
        onUpdate();
      } catch (error) {
        console.error(error);
        showToast("Erro ao excluir", { severity: "error" });
      }
    }
  };

  const sectionLabel =
    sections.find((s) => s.id === sectionId)?.label || sectionId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Gerenciar {sectionLabel}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenForm()}
          >
            Adicionar Item
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ordem</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Descrição</TableCell>
                {sectionId === "gallery" && <TableCell>Imagem</TableCell>}
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.display_order}</TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>
                    {row.description && row.description.length > 50
                      ? row.description.substring(0, 50) + "..."
                      : row.description}
                  </TableCell>
                  {sectionId === "gallery" && (
                    <TableCell>
                      {row.image_url && (
                        <img
                          src={row.image_url}
                          alt="preview"
                          style={{ height: 40, borderRadius: 4 }}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <IconButton
                      onClick={() => handleOpenForm(row)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(row.id)}
                      size="small"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhum item encontrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Nested Dialog for Add/Edit */}
      <Dialog
        open={openFormDialog}
        onClose={() => setOpenFormDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingItem ? "Editar Item" : "Novo Item"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Ordem"
              type="number"
              value={formData.display_order}
              onChange={(e) =>
                setFormData({ ...formData, display_order: +e.target.value })
              }
              fullWidth
            />
            {sectionId !== "gallery" && (
              <>
                <TextField
                  label="Título"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  fullWidth
                />
                <TextField
                  label="Descrição"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  multiline
                  rows={3}
                  fullWidth
                />
              </>
            )}
            {(sectionId === "benefits" || sectionId === "categories") && (
              <TextField
                label="Nome do Ícone (Lucide React)"
                value={formData.icon}
                onChange={(e) =>
                  setFormData({ ...formData, icon: e.target.value })
                }
                helperText="Ex: TrendingUp, Handshake, Award"
                fullWidth
              />
            )}
            {(sectionId === "gallery" || sectionId === "hero") && (
              <Box>
                <TextField
                  label="Título"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Descrição"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="URL da Imagem"
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, image_url: e.target.value })
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <Button variant="outlined" component="label" fullWidth>
                  Upload de Imagem
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Button>
                {selectedFile && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Arquivo selecionado: {selectedFile.name}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

// --- Main Component ---

export default function WelcomeManager() {
  const [heroData, setHeroData] = useState<any>(null);
  const [stats, setStats] = useState<any[]>([]);
  const [benefits, setBenefits] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [aboutData, setAboutData] = useState<any[]>([]);
  const [organizationData, setOrganizationData] = useState<any[]>([]);
  const [impactData, setImpactData] = useState<any[]>([]);

  const [managerOpen, setManagerOpen] = useState(false);
  const [currentSectionId, setCurrentSectionId] = useState("hero");
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchAllData = useCallback(async () => {
    try {
      const [
        heroRes,
        galleryRes,
        benefitsRes,
        statsRes,
        categoriesRes,
        aboutRes,
        organizationRes,
        impactRes,
      ] = await Promise.all([
        getWelcomeContent("hero"),
        getWelcomeContent("gallery"),
        getWelcomeContent("benefits"),
        getWelcomeContent("stats"),
        getWelcomeContent("categories"),
        getWelcomeContent("about"),
        getWelcomeContent("organization"),
        getWelcomeContent("impact"),
      ]);

      setHeroData(heroRes.length > 0 ? heroRes[0] : null);
      setImages(
        galleryRes.length > 0 ? galleryRes.map((i: any) => i.image_url) : []
      );
      setBenefits(benefitsRes);
      setStats(statsRes);
      setCategories(categoriesRes);
      setAboutData(aboutRes);
      setOrganizationData(organizationRes);
      setImpactData(impactRes);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData, refreshKey]);

  const handleManage = (sectionId: string) => {
    setCurrentSectionId(sectionId);
    setManagerOpen(true);
  };

  // Defaults for preview (to show layout even if empty)
  const defaultHeroImage =
    "https://praceria.com.br/wp-content/uploads/2025/07/1.jpeg";

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "bold", color: "#1e293b" }}>
          Editor Visual da Home
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Passe o mouse sobre as seções para editar
        </Typography>
      </Box>

      {/* Preview Container */}
      <div className="max-w-[1200px] mx-auto bg-white shadow-xl rounded-xl overflow-hidden p-6 border border-slate-200">
        {/* HERO SECTION */}
        <div className="relative group mb-8">
          <SectionOverlay title="Hero" onEdit={() => handleManage("hero")} />
          <section className="relative overflow-hidden rounded-2xl min-h-[400px] flex items-center justify-center">
            <img
              src={heroData?.image_url || defaultHeroImage}
              alt="Hero"
              className="absolute inset-0 w-full h-full object-cover filter grayscale-[20%]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#24324D]/40 to-[#151c2c]/80" />
            <div className="relative z-10 text-center text-white px-6 flex flex-col items-center py-12">
              <img src={logo} alt="Logo" className="h-24 w-auto mb-4" />
              <h1 className="text-3xl font-bold tracking-wide">
                {heroData?.title || "Conheça a Praceria"}
              </h1>
              <p className="mt-2 text-white/80 max-w-xl text-lg mx-auto leading-relaxed">
                {heroData?.description ||
                  "Conectamos expositores autorais para criar eventos únicos."}
              </p>
            </div>
          </section>
        </div>

        {/* STATS SECTION */}
        <div className="relative group mb-8">
          <SectionOverlay
            title="Estatísticas"
            onEdit={() => handleManage("stats")}
          />
          <section className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x border rounded-lg bg-white">
            {stats.length > 0 ? (
              stats.map((stat, i) => (
                <div key={i} className="text-center p-6">
                  <div className="text-[#24324D] text-3xl font-light">
                    {stat.description}
                  </div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 mt-1">
                    {stat.title}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 col-span-4 text-center text-gray-400 italic">
                Sem estatísticas cadastradas (Ex: 70+ Marcas)
              </div>
            )}
          </section>
        </div>

        {/* BENEFITS SECTION */}
        <div className="relative group mb-8">
          <SectionOverlay
            title="Benefícios"
            onEdit={() => handleManage("benefits")}
          />
          <section className="mt-10">
            <h2 className="text-2xl font-light text-center mb-6 text-[#24324D]">
              Por que fazer parte?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.length > 0 ? (
                benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#24324D] text-white flex items-center justify-center rounded-md shrink-0">
                      <RenderIcon iconName={benefit.icon} />
                    </div>
                    <div>
                      <div className="text-[#24324D] font-medium mb-1">
                        {benefit.title}
                      </div>
                      <div className="text-slate-600 text-sm leading-relaxed">
                        {benefit.description}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center text-gray-400 italic">
                  Sem benefícios cadastrados
                </div>
              )}
            </div>
          </section>
        </div>

        {/* CATEGORIES SECTION */}
        <div className="relative group mb-8">
          <SectionOverlay
            title="Categorias"
            onEdit={() => handleManage("categories")}
          />
          <section className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.length > 0 ? (
              categories.map((category, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-4 bg-teal-600 text-white rounded-md"
                >
                  <RenderIcon iconName={category.icon} className="w-6 h-6" />
                  <span className="font-medium">{category.title}</span>
                </div>
              ))
            ) : (
              <div className="col-span-4 text-center text-gray-400 italic">
                Sem categorias cadastradas
              </div>
            )}
          </section>
        </div>

        {/* ABOUT & ORGANIZATION SECTION */}
        <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 items-start mb-8">
          <div className="md:col-span-2 relative group">
            <SectionOverlay
              title="Sobre"
              onEdit={() => handleManage("about")}
            />
            <div>
              <h3 className="text-xl font-light mb-4 text-[#24324D]">
                {aboutData.length > 0
                  ? aboutData[0].title
                  : "Você em boa companhia"}
              </h3>
              {aboutData.map((item, i) => (
                <p key={i} className="text-[#374151] leading-8 mb-3">
                  {item.description}
                </p>
              ))}
              {aboutData.length === 0 && (
                <p className="text-gray-400 italic">
                  Sem descrição "Sobre" cadastrada.
                </p>
              )}
            </div>
          </div>
          <div className="relative group">
            <SectionOverlay
              title="Organização"
              onEdit={() => handleManage("organization")}
            />
            <div>
              <h4 className="text-lg font-light mb-4 text-[#24324D]">
                {organizationData.length > 0
                  ? organizationData[0].title
                  : "Como tudo é organizado?"}
              </h4>
              <ul className="space-y-3">
                {organizationData.map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-1 h-1 bg-[#24324D] shrink-0" />
                    <span className="text-[#374151] text-sm">
                      {item.description}
                    </span>
                  </li>
                ))}
                {organizationData.length === 0 && (
                  <li className="text-gray-400 italic">
                    Sem itens de organização.
                  </li>
                )}
              </ul>
            </div>
          </div>
        </section>

        {/* IMPACT SECTION */}
        <div className="relative group mb-8">
          <SectionOverlay
            title="Impacto Social"
            onEdit={() => handleManage("impact")}
          />
          <section className="mt-12 text-center border rounded-lg p-8 bg-slate-50">
            <h3 className="text-xl font-light text-[#24324D] mb-3">
              {impactData.length > 0
                ? impactData[0].title
                : "Impacto Social Real"}
            </h3>
            {impactData.map((item, i) => (
              <p
                key={i}
                className="text-[#374151] leading-7 max-w-xl mx-auto mb-2"
              >
                {item.description}
              </p>
            ))}
            {impactData.length === 0 && (
              <p className="text-gray-400 italic">
                Sem conteúdo de impacto social.
              </p>
            )}
          </section>
        </div>

        {/* GALLERY SECTION */}
        <div className="relative group mb-8">
          <SectionOverlay
            title="Galeria"
            onEdit={() => handleManage("gallery")}
          />
          <section className="mt-12">
            <h3 className="text-xl font-light text-center text-[#24324D] mb-2">
              Galeria de Fotos
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {images.length > 0 ? (
                images.map((image, i) => (
                  <img
                    key={i}
                    src={image}
                    alt="Gallery"
                    className="w-full h-40 object-cover rounded"
                  />
                ))
              ) : (
                <div className="col-span-4 text-center text-gray-400 italic py-8 border-2 border-dashed rounded">
                  Sem imagens na galeria
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <SectionManager
        sectionId={currentSectionId}
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
        onUpdate={() => setRefreshKey((k) => k + 1)}
      />
    </Box>
  );
}
